import React from 'react';
import {
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  Row,
  Col,
  Card,
  Tag,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { ItemSearchSelect } from '../ItemSearchSelect';

const { Option } = Select;

export interface CycleLogOutputsSectionProps {
  form: any;
  outputsData: any[];
  totalOutputs: number;
  storehouses: any[];
  storehouseId?: any;
  allItems: any[];
  dynamicItems: any[];
  setDynamicItems: React.Dispatch<React.SetStateAction<any[]>>;
  register: any;
  isMobile?: boolean;
}

export const CycleLogOutputsSection: React.FC<CycleLogOutputsSectionProps> = ({
  form,
  outputsData,
  totalOutputs,
  storehouses,
  storehouseId,
  allItems,
  dynamicItems,
  setDynamicItems,
  register,
  isMobile = false,
}) => {
  return (
    <Card
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>Producción a Recolectar</span>
          {totalOutputs > 0 && (
            <span
              style={{
                fontSize: '14px',
                color: '#52c41a',
                fontWeight: 'bold',
              }}
            >
              Total Estimado: ${' '}
              {totalOutputs.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          )}
        </div>
      }
      style={{ marginBottom: 16 }}
      size="small"
    >
      {storehouses.length > 1 && (
        <Form.Item
          name="storehouse_id"
          label="Almacén de Destino (Producción)"
          preserve={true}
          rules={[
            {
              required: outputsData.some(
                (o: any) => o?.register_type === 'PRODUCT' && o?.element_id
              ),
              message: 'Seleccione el almacén de destino',
            },
          ]}
        >
          <Select placeholder="Seleccionar bodega">
            {storehouses.map((s: any) => (
              <Option key={s.id} value={s.id}>
                {s.name || s.alias}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {storehouses.length === 1 && (
        <Form.Item
          name="storehouse_id"
          initialValue={storehouses[0].id}
          preserve={true}
          hidden
        >
          <Input />
        </Form.Item>
      )}
      <Form.List name="outputs" initialValue={[{}]}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => {
              const currentOutput = outputsData?.[name] || {};
              const selectedElementId = currentOutput.element_id;
              const selectedItem = allItems.find(
                (i: any) => i.id === selectedElementId
              );
              const itemCode = selectedItem?.custom_id || selectedItem?.sku;
              const categoryLabel =
                register?.categoryOptions?.find(
                  (c: any) => c.key === selectedItem?.category
                )?.string_value || selectedItem?.category;
              const umLabel =
                register?.umOptions?.find(
                  (u: any) => u.key === selectedItem?.measure_unit
                )?.string_value || selectedItem?.measure_unit;

              const storeStock =
                selectedItem && storehouseId
                  ? selectedItem.inventory?.find(
                      (inv: any) =>
                        String(inv.id) === String(storehouseId) ||
                        String(inv.uid) === String(storehouseId) ||
                        String(inv.storehouse_id) === String(storehouseId)
                    )?.quantity ?? 0
                  : selectedItem?.inventory?.reduce(
                      (sum: number, inv: any) => sum + (inv.quantity || 0),
                      0
                    ) ??
                    selectedItem?.quantity ??
                    0;

              return (
                <div
                  key={key}
                  style={{
                    marginBottom: 12,
                    background: '#f5fff5',
                    padding: isMobile ? '8px 6px' : 10,
                    border: '1px solid #d9f7be',
                    borderRadius: 6,
                  }}
                >
                  <Row gutter={[8, 4]} align="bottom">
                    <Col xs={24} sm={12} md={9}>
                      <Form.Item
                        {...restField}
                        name={[name, 'element_id']}
                        label="Producto (Resultado)"
                        rules={[{ required: true, message: 'Requerido' }]}
                        style={{ marginBottom: 4 }}
                      >
                        <ItemSearchSelect
                          placeholder="Buscar producto a cosechar..."
                          itemType="SELL"
                          initialItems={allItems}
                          selectedStorehouseId={storehouseId}
                          onItemSelect={(item) => {
                            if (item) {
                              if (!dynamicItems.some((i) => i.id === item.id)) {
                                setDynamicItems((prev) => [...prev, item]);
                              }
                              const itemPrice =
                                Number(item.sell_price) ||
                                Number(item.purchase_price) ||
                                0;
                              const cur =
                                form.getFieldValue(['outputs', name]) || {};
                              form.setFieldValue(['outputs', name], {
                                ...cur,
                                element_id: item.id,
                                element_uid: item.uid,
                                name: item.name,
                                item_name: item.name,
                                item_code:
                                  item.custom_id || item.sku || '',
                                item_description: item.description || '',
                                register_type:
                                  item.register_type || 'PRODUCT',
                                unit_price: itemPrice,
                              });
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                      <Form.Item
                        {...restField}
                        name={[name, 'quantity']}
                        label="Cant."
                        rules={[{ required: true }]}
                        style={{ marginBottom: 4 }}
                      >
                        <InputNumber
                          min={0.01}
                          style={{ width: '100%' }}
                          suffix={selectedItem?.measure_unit || undefined}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={6} md={5}>
                      <Form.Item
                        {...restField}
                        name={[name, 'unit_price']}
                        label="Valor U. Est."
                        tooltip="Para costeo"
                        style={{ marginBottom: 4 }}
                      >
                        <InputNumber
                          min={0}
                          style={{ width: '100%' }}
                          formatter={(value) =>
                            `$ ${value}`.replaceAll(
                              /\B(?=(\d{3})+(?!\d))/g,
                              ','
                            )
                          }
                          parser={(value) =>
                            value
                              ? (value.replaceAll(/\$\s?|(,*)/g, '') as any)
                              : ''
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={18} sm={8} md={4}>
                      <Form.Item
                        label="Total Estimado"
                        style={{ marginBottom: 4 }}
                      >
                        <span
                          style={{
                            fontWeight: 'bold',
                            display: 'inline-block',
                            paddingTop: 5,
                            color: '#52c41a',
                            fontSize: 13,
                          }}
                        >
                          ${' '}
                          {(
                            (Number(currentOutput?.quantity) || 0) *
                            (Number(currentOutput?.unit_price) || 0)
                          ).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </Form.Item>
                    </Col>
                    <Col
                      xs={6}
                      sm={4}
                      md={2}
                      style={{ textAlign: 'center', paddingBottom: 5 }}
                    >
                      <Form.Item
                        label=" "
                        style={{ marginBottom: 4, padding: 0 }}
                      >
                        <Button
                          danger
                          type="text"
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Fila detallada de metadatos del producto obtenido */}
                  {selectedItem && (
                    <div
                      style={{
                        marginTop: 6,
                        padding: '4px 8px',
                        background: '#fff',
                        borderRadius: 4,
                        border: '1px solid #b7eb8f',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 11,
                      }}
                    >
                      <Tag
                        color="cyan"
                        style={{
                          margin: 0,
                          fontSize: 10,
                          lineHeight: '18px',
                          padding: '0 5px',
                        }}
                      >
                        Producto Terminado
                      </Tag>
                      {itemCode && (
                        <Tag
                          bordered={false}
                          style={{
                            margin: 0,
                            fontSize: 10,
                            background: '#f5f5f5',
                            color: '#595959',
                            padding: '0 4px',
                          }}
                        >
                          Ref: <strong>{itemCode}</strong>
                        </Tag>
                      )}
                      {categoryLabel && categoryLabel !== 'VRS' && (
                        <Tag
                          bordered={false}
                          style={{
                            margin: 0,
                            fontSize: 10,
                            background: '#f5f5f5',
                            color: '#595959',
                            padding: '0 4px',
                          }}
                        >
                          Cat: {categoryLabel}
                        </Tag>
                      )}
                      {selectedItem.measure_unit && (
                        <Tag
                          bordered={false}
                          style={{
                            margin: 0,
                            fontSize: 10,
                            background: '#e6f7ff',
                            color: '#096dd9',
                            padding: '0 4px',
                          }}
                        >
                          UM:{' '}
                          <strong>
                            {umLabel || selectedItem.measure_unit}
                          </strong>
                        </Tag>
                      )}
                      <span style={{ color: '#389e0d', fontWeight: 600 }}>
                        Stock actual en Bodega: {storeStock}{' '}
                        {umLabel || selectedItem.measure_unit}
                      </span>
                      {selectedItem.sell_price ? (
                        <span
                          style={{
                            color: '#8c8c8c',
                            marginLeft: isMobile ? 0 : 'auto',
                            width: isMobile ? '100%' : 'auto',
                          }}
                        >
                          Precio venta ref: $
                          {Number(selectedItem.sell_price).toLocaleString(
                            'en-US',
                            { minimumFractionDigits: 2 }
                          )}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
            <Form.Item>
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                Agregar Producto
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </Card>
  );
};
